import { ReactNode, useState } from "react";
import { useLocation } from "react-router";
import {
  getParentNavigation,
  ParentNavigation,
  ParentNavigationContext,
} from "../../hooks/useParentNavigation";

type ActiveParentNavigation = {
  childRootPathname: string;
  parentNavigation: ParentNavigation;
};

type ParentNavigationSnapshot = {
  activeNavigation: ActiveParentNavigation | null;
  locationKey: string;
};

const isWithinChildRoute = (
  pathname: string,
  childRootPathname: string,
): boolean =>
  pathname === childRootPathname ||
  pathname.startsWith(`${childRootPathname}/`);

export const ParentNavigationProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const location = useLocation();
  const incomingParentNavigation = getParentNavigation(location.state);
  const [snapshot, setSnapshot] = useState<ParentNavigationSnapshot>(() => ({
    activeNavigation: incomingParentNavigation
      ? {
          childRootPathname: location.pathname,
          parentNavigation: incomingParentNavigation,
        }
      : null,
    locationKey: location.key,
  }));

  let currentSnapshot = snapshot;

  if (location.key !== snapshot.locationKey) {
    const retainedNavigation = snapshot.activeNavigation;
    const returnedToParent =
      retainedNavigation?.parentNavigation.key === location.key;
    const remainsWithinChildRoute =
      retainedNavigation &&
      isWithinChildRoute(
        location.pathname,
        retainedNavigation.childRootPathname,
      );
    const activeNavigation = incomingParentNavigation
      ? {
          childRootPathname: location.pathname,
          parentNavigation: incomingParentNavigation,
        }
      : !returnedToParent && remainsWithinChildRoute
        ? retainedNavigation
        : null;

    currentSnapshot = {
      activeNavigation,
      locationKey: location.key,
    };
    setSnapshot(currentSnapshot);
  }

  return (
    <ParentNavigationContext.Provider
      value={currentSnapshot.activeNavigation?.parentNavigation ?? null}
    >
      {children}
    </ParentNavigationContext.Provider>
  );
};
